import { NetworkSpeedEndpoint } from "@common/models/Analytics.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { fetchAllPages } from "@main/pages/Release/helpers/fetchAllPages";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export interface NetworkSpeedItem {
  score: number | undefined | null;
  method: string;
  url: string;
  time: number;
  isError: boolean;
  isWarning: boolean;
}

export default function useNetworkSpeedData() {
  const [searchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version") ?? "";

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { analyticsService } = useAnalyticsDeps();

  async function getNetworkSpeedDetails(signal?: AbortSignal): Promise<NetworkSpeedEndpoint[]> {
    if (!selectedWorkspace || !selectedApp || !selectedVersion) return [];

    return fetchAllPages(100, (offset, limit) =>
      analyticsService
        .getNetworkSpeedDetails(selectedWorkspace.id, selectedApp.id, selectedVersion, offset, limit, signal)
        .then(({ data }) => ({ items: data.items ?? [], total: data.total })),
    );
  }

  const { data: networkSpeedData, isLoading } = useQuery(
    ["networkSpeed", { selectedApp, selectedVersion }],
    ({ signal }) => getNetworkSpeedDetails(signal),
    {
      enabled: Boolean(selectedVersion),
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const endpoints: NetworkSpeedItem[] | undefined = networkSpeedData?.map((endpoint): NetworkSpeedItem => {
    return {
      score: endpoint.score,
      method: endpoint.method,
      url: endpoint.endpoint,
      time: endpoint.average_duration,
      isError: endpoint.is_error ?? false,
      isWarning: false,
    };
  });

  return {
    endpoints,
    isLoading,
  };
}
